import type { Row } from "@tanstack/react-table";
import React from "react";
import { Link } from "react-router-dom";

import BooleanIcon from "veneer/BooleanIcon";
import ContextMenu, { ContextMenuItem } from "veneer/ContextMenu";
import { ColumnDef, createColumnHelper, sorting } from "veneer/DataTable";

import { getLastConnectedStatusMeta } from "cortex/ui/src/constant/lastConnected";
import { DeviceStatusIcon } from "cortex/ui/src/ui/table/component/DeviceStatusIcon";
import { formatDateTimeLocal } from "cortex/ui/src/ui/table/formats";

import LastConnected from "./LastConnected";
import LastConnectedHeader from "./LastConnectedHeader";
import VersionColumn from "./VersionColumn";
import type { Device } from "../model";
import { formatOsBadge } from "../tableFormat";
import DeviceTags from "../tags";

const column = createColumnHelper<Device>();

function statusSort<T>(a: Row<T>, b: Row<T>, columnId: string): number {
  function statusRank(s: Device["status"] | undefined): number {
    if (s?.online) return 0;
    if (s?.hibernating) return 1;
    return 2;
  }

  const aStatus = a.getValue(columnId) as Device["status"] | undefined;
  const bStatus = b.getValue(columnId) as Device["status"] | undefined;

  return statusRank(aStatus) - statusRank(bStatus);
}

interface ColumnsDeps {
  getContextMenuItems: (row: Device) => ReadonlyArray<ContextMenuItem>;
  isStrategiesEnabled: boolean;
}

export function getColumns({
  getContextMenuItems,
  isStrategiesEnabled,
}: ColumnsDeps): ReadonlyArray<ColumnDef<Device, any>> {
  return [
    column.accessor("status", {
      header: "Status",
      cell: (prop) => <DeviceStatusIcon device={prop.row.original} />,
      meta: { width: 100 },
      sortingFn: statusSort,
    }),
    column.accessor("last_seen", {
      header: () => <LastConnectedHeader />,
      cell: (prop) => <LastConnected lastSeen={prop.getValue() ?? null} />,
      meta: {
        settingsLabel: "Connectivity",
      },
      sortingFn: sorting.datetime((row) => row.last_seen?.value),
      sortUndefined: "last",
    }),
    column.accessor((row) => row.last_seen, {
      id: "last_connected",
      header: "Last Connected",
      cell: ({ getValue }) => {
        const lastSeen = getValue();

        if (lastSeen?.neverConnected) {
          return "Never";
        }

        const lastSeenAt = lastSeen?.value;
        const status = getLastConnectedStatusMeta(lastSeen ?? null);

        if (
          !lastSeenAt ||
          (status.label === "Unreachable" && status.sublabel === "4w+")
        ) {
          return "4w+";
        }

        return formatDateTimeLocal(lastSeenAt, "MMM Do, YYYY hh:mm:ss a");
      },
      sortingFn: sorting.datetime((row) => row.last_seen?.value),
      sortUndefined: "last",
    }),
    column.accessor("name", {
      header: "Name",
      cell: (prop) => {
        const row = prop.row.original;
        const value = prop.getValue();
        return <Link to={`/devices/${row.device_id}`}>{value}</Link>;
      },
      meta: { primary: true },
      sortingFn: "alphanumeric",
    }),
    column.accessor("strategy_name", {
      header: "Strategy",
      cell: (prop) => {
        const row = prop.row.original;
        const value = prop.getValue();
        if (!value) return "";
        if (!isStrategiesEnabled) return value;

        return (
          <Link to={`/v2/strategies/${row.strategy_id}/edit`}>
            {value || "No Strategy"}
          </Link>
        );
      },
      sortingFn: "alphanumeric",
    }),
    column.accessor("network_name", {
      header: "Network",
      cell: (prop) => {
        const row = prop.row.original;
        const value = prop.getValue();
        return <Link to={`/networks/${row.network_id}`}>{value}</Link>;
      },
      sortingFn: "alphanumeric",
    }),
    column.accessor("current_version", {
      header: "Version",
      cell: (prop) => <VersionColumn device={prop.row.original} />,
      sortingFn: "alphanumeric",
    }),
    column.accessor("tags", {
      header: "Tags",
      cell: (prop) => {
        const tags = prop.getValue();
        if (!tags || tags.length === 0) return "";
        return <DeviceTags hideSystemTags={true} tags={tags} />;
      },
      enableSorting: false,
    }),
    column.accessor("os_platform", {
      header: "OS",
      cell: (prop) => formatOsBadge(prop.getValue()),
      meta: { width: 175 },
      sortingFn: "alphanumeric",
    }),
    column.accessor("is_agent", {
      header: "Agent",
      cell: (prop) => {
        return <BooleanIcon value={prop.getValue()} />;
      },
      meta: { width: 100 },
      sortingFn: sorting.boolean((row: Device) => row.is_agent),
    }),
    column.accessor("testing", {
      header: "Testing",
      cell: (prop) => {
        return <BooleanIcon value={prop.getValue()} />;
      },
      meta: { width: 100 },
      sortingFn: sorting.boolean((row: Device) => row.testing),
    }),
    column.accessor("registration_date", {
      header: "Registration Date",
      cell: (prop) => formatDateTimeLocal(prop.getValue()),
      sortingFn: sorting.datetime((row: Device) => row.registration_date),
    }),
    column.accessor("network_change_date", {
      header: "Network Change Date",
      cell: (prop) => formatDateTimeLocal(prop.getValue()),
      sortingFn: sorting.datetime((row: Device) => row.network_change_date),
    }),
    column.display({
      id: "actions",
      header: "Actions",
      cell: (prop) => {
        const row = prop.row.original;
        const items = [...getContextMenuItems(row)];
        return <ContextMenu enableClickPropagation items={items} />;
      },
      meta: { width: 100 },
    }),
  ];
}
